import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useOutletContext, useSearchParams } from 'react-router-dom'
import { Layers, ListMusic, Plus, RefreshCw, Upload } from 'lucide-react'
import {
  analyzeAudioTrackLabels,
  createAudioGroup,
  deleteAudioGroup,
  deleteAudioTrack,
  deleteAudioTrackImage,
  deleteAudioTrackLosslessSource,
  getAudioGroups,
  getAudioTrack,
  getAudioTrackBindings,
  getAudioTracks,
  reprocessAudioTrack,
  clearAudioTrackBpm,
  updateAudioGroup,
  updateAudioTrack,
  updateAudioTrackBinding,
  updateAudioTrackBpm,
  updateAudioTrackGroups,
  uploadAudioFile,
  uploadAudioTrackImage,
  uploadAudioTrackLosslessSource,
  type AudioGroup,
  type AudioTrack,
  type AudioTrackBindingItem,
  type AudioTrackFilters
} from '../api'
import {
  getAudioTrackLabels,
  getLabels,
  rejectAudioTrackLabel,
  suggestAudioLabelsFromPrompt,
  suggestAudioTrackLabels,
  updateAudioTrackLabels,
  type AudioTrackLabel,
  type AudioTrackLabelSummary,
  type Label
} from '../api/labels'
import {
  createSunoPrompt,
  findMatchingSunoPrompt,
  getSunoPromptGroups,
  linkSunoPromptTracks,
  unlinkAudioTrackSunoPrompt,
  type SunoPromptGroup
} from '../api/suno'
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog'
import { useToast } from '../components/ToastProvider'
import { Button, Tabs, adminMediaPlayerClassName } from '../components/ui'
import { areAudioFiltersEqual, audioFiltersToSearchParams, getAudioFiltersKey, searchParamsToAudioFilters } from '../features/audio/audioFilterParams'
import { AudioAssignments } from '../features/audio/AudioAssignments'
import { AudioBindDialog } from '../features/audio/AudioBindDialog'
import { AudioGroupDialog } from '../features/audio/AudioGroupDialog'
import { AudioGroups } from '../features/audio/AudioGroups'
import { AudioTrackDialog } from '../features/audio/AudioTrackDialog'
import { AudioTracks } from '../features/audio/AudioTracks'
import { useAudioPlayerContext } from '../features/audio/AudioPlayerProvider'
import type { PromptEditorState } from '../features/suno/PromptEditorDialog'
import type { AdminOutletContext } from '../layouts/AdminLayout'
import styles from './AudioPage.module.css'

type AudioTab = 'tracks' | 'groups' | 'assignments'

type AudioPageProps = {
  tab: AudioTab
}

const mobileAudioPageQuery = '(max-width: 900px)'

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ')

const pc = adminMediaPlayerClassName

const createEmptyAudioGroup = (): AudioGroup => ({
  id: '',
  name: '',
  description: '',
  track_count: 0,
  channel_count: 0,
  created_at: '',
  updated_at: ''
})

const wait = async (delayMs: number): Promise<void> => {
  await new Promise((resolve) => window.setTimeout(resolve, delayMs))
}

const toPlayableAudioTrack = (track: AudioTrackBindingItem): AudioTrack => ({
  ...track,
  audio_analysis: {
    completed_count: 0,
    last_error: null,
    latest_completed_at: null,
    latest_failed_at: null,
    latest_run_id: null,
    pending_job_id: null,
    pending_suggestion_count: 0,
    rejected_suggestion_count: 0,
    status: 'never_run'
  },
  bpm_analyzed_at: null,
  bpm_confidence: null,
  bpm_correction: 'none',
  bpm_correction_reason: null,
  corrected_bpm: null,
  detected_bpm: null,
  file_hash: '',
  playback_bitrate_kbps: null,
  playback_error: null,
  playback_format: track.file_url ? 'mp3' : null,
  playback_generated_at: null,
  playback_source_kind: track.file_url ? 'uploaded' : 'absent',
  playback_status: track.file_url ? 'ready' : 'missing',
  lossless_duration_seconds: null,
  lossless_error: null,
  lossless_format: null,
  lossless_loudness_lufs: null,
  lossless_object_key: null,
  lossless_size_bytes: null,
  lossless_storage_backend: 'local_filesystem',
  lossless_status: 'missing',
  lossless_true_peak_db: null,
  lossless_uploaded_at: null,
  manual_bpm: null,
  manual_bpm_source: null,
  mime_type: null,
  processing_summary: {
    blocking_completed: true,
    completed_step_count: 0,
    current_step_error: null,
    current_step_status: null,
    current_step_type: null,
    run_id: null,
    status: 'none',
    total_step_count: 0,
    trigger: null
  },
  raw_bpm: null,
  size_bytes: null,
  suno_prompt_binding: null,
  style_prompt: null
})

export const AudioPage = ({
  tab
}: AudioPageProps) => {
  const { toast } = useToast()
  const { closeMobileActions, setMobileMenuItems } = useOutletContext<AdminOutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo(() => searchParamsToAudioFilters(searchParams), [searchParams])
  const filtersKey = useMemo(() => getAudioFiltersKey(filters), [filters])
  const [tracks, setTracks] = useState<AudioTrack[]>([])
  const [totalTracks, setTotalTracks] = useState(0)
  const [groups, setGroups] = useState<AudioGroup[]>([])
  const [labels, setLabels] = useState<Label[]>([])
  const [tracksLoading, setTracksLoading] = useState(true)

  const dashboardRequestIdRef = useRef(0)

  const loadDashboard = useCallback(async (loadFilters: AudioTrackFilters): Promise<AudioGroup[] | null> => {
    const requestId = dashboardRequestIdRef.current + 1
    dashboardRequestIdRef.current = requestId
    setTracksLoading(true)

    try {
      const [tracksPayload, groupsPayload] = await Promise.all([
        getAudioTracks(loadFilters),
        getAudioGroups()
      ])

      if (dashboardRequestIdRef.current !== requestId) {
        return null
      }

      setTracks(tracksPayload.items)
      setTotalTracks(tracksPayload.total)
      setGroups(groupsPayload)
      return groupsPayload
    } finally {
      if (dashboardRequestIdRef.current === requestId) {
        setTracksLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void loadDashboard(filters)
  }, [filtersKey, loadDashboard])

  useEffect(() => {
    void getLabels({ status: 'active' }).then(setLabels)
  }, [])

  const [uploading, setUploading] = useState(false)
  const [selectedTrack, setSelectedTrack] = useState<AudioTrack | null>(null)
  const [bindingTrack, setBindingTrack] = useState<AudioTrack | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<AudioGroup | null>(null)
  const [trackToDelete, setTrackToDelete] = useState<AudioTrack | null>(null)
  const [groupToDelete, setGroupToDelete] = useState<AudioGroup | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [mobileUploadAction, setMobileUploadAction] = useState<(() => void) | null>(null)
  const [mobileCreateGroupAction, setMobileCreateGroupAction] = useState<(() => void) | null>(null)
  const [isMobileAudioPage, setIsMobileAudioPage] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(mobileAudioPageQuery).matches
  )
  const [trackOverrides, setTrackOverrides] = useState<Record<string, AudioTrack>>({})
  const [sunoPromptGroups, setSunoPromptGroups] = useState<SunoPromptGroup[]>([])

  const {
    playingTrack,
    audioLoading,
    audioPlaying,
    markTrackDeleted,
    playLibraryTrack,
    playTrack: playStandaloneTrack
  } = useAudioPlayerContext()

  const visibleTracks = tracks.map((track) => trackOverrides[track.id] ?? track)

  const updateVisibleTrack = useCallback((track: AudioTrack): void => {
    setTrackOverrides((current) => ({
      ...current,
      [track.id]: track
    }))
    setSelectedTrack((current) => current?.id === track.id ? track : current)
    setBindingTrack((current) => current?.id === track.id ? track : current)
  }, [])

  useEffect(() => {
    setTrackOverrides({})
  }, [tracks])

  useEffect(() => {
    if (selectedTrack) {
      void getSunoPromptGroups().then(setSunoPromptGroups).catch(() => {})
    }
  }, [selectedTrack])

  useEffect(() => {
    const mediaQuery = window.matchMedia(mobileAudioPageQuery)
    const syncMobileAudioPage = (): void => setIsMobileAudioPage(mediaQuery.matches)

    syncMobileAudioPage()
    mediaQuery.addEventListener('change', syncMobileAudioPage)

    return () => mediaQuery.removeEventListener('change', syncMobileAudioPage)
  }, [])

  const refreshTrackAfterReprocess = async (trackId: string): Promise<void> => {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      await wait(2500)
      const track = await getAudioTrack(trackId)
      updateVisibleTrack(track)

      if (
        track.processing_summary.status === 'completed'
        || track.processing_summary.status === 'failed'
        || track.status === 'failed'
        || track.status === 'unsupported'
      ) {
        return
      }
    }
  }

  const toTrackLabelSummary = (assignment: AudioTrackLabel): AudioTrackLabelSummary => ({
    id: assignment.label.id,
    category: assignment.label.category,
    name: assignment.label.name,
    slug: assignment.label.slug,
    source: assignment.source,
    status: assignment.status
  })

  const registerMobileUploadAction = useCallback((action: (() => void) | null): void => {
    setMobileUploadAction(() => action)
  }, [])

  const registerMobileCreateGroupAction = useCallback((action: (() => void) | null): void => {
    setMobileCreateGroupAction(() => action)
  }, [])

  const openCreateGroupDialog = useCallback((): void => {
    setSelectedGroup(createEmptyAudioGroup())
  }, [])

  useEffect(() => {
    setMobileMenuItems(
      <>
        {tab === 'tracks' && mobileUploadAction && (
          <button
            className="mobile-actions-item"
            onClick={() => {
              closeMobileActions()
              mobileUploadAction()
            }}
            role="menuitem"
            type="button"
          >
            <Upload size={17} />
            <span>Upload audio</span>
          </button>
        )}
        {tab === 'groups' && mobileCreateGroupAction && (
          <button
            className="mobile-actions-item"
            onClick={() => {
              closeMobileActions()
              mobileCreateGroupAction()
            }}
            role="menuitem"
            type="button"
          >
            <Plus size={17} />
            <span>Create group</span>
          </button>
        )}
        <NavLink
          className={tab === 'tracks' ? 'mobile-actions-item active' : 'mobile-actions-item'}
          end
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/audio"
        >
          <ListMusic size={17} />
          <span>Tracks</span>
        </NavLink>
        <NavLink
          className={tab === 'groups' ? 'mobile-actions-item active' : 'mobile-actions-item'}
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/audio/groups"
        >
          <Layers size={17} />
          <span>Groups</span>
        </NavLink>
        <NavLink
          className={tab === 'assignments' ? 'mobile-actions-item active' : 'mobile-actions-item'}
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/audio/assignments"
        >
          <ListMusic size={17} />
          <span>Assignments</span>
        </NavLink>
      </>
    )

    return () => setMobileMenuItems(null)
  }, [closeMobileActions, mobileCreateGroupAction, mobileUploadAction, setMobileMenuItems, tab])

  const playTrack = useCallback((track: AudioTrack): void => {
    if (track.status !== 'ready') {
      return
    }

    void playLibraryTrack(track, filters)
  }, [filters, playLibraryTrack])

  const applyAudioFilters = useCallback(async (nextFilters: AudioTrackFilters): Promise<void> => {
    if (areAudioFiltersEqual(filters, nextFilters)) {
      return
    }

    setSearchParams(audioFiltersToSearchParams(nextFilters), { replace: false })
  }, [filters, setSearchParams])

  const refresh = async (): Promise<void> => {
    await loadDashboard(filters)
    toast('Audio library refreshed.')
  }

  const confirmDeleteTrack = async (): Promise<void> => {
    if (!trackToDelete) {
      return
    }

    setDeleting(true)
    try {
      await deleteAudioTrack(trackToDelete.id)
      markTrackDeleted(trackToDelete.id)
      toast(`Deleted track "${trackToDelete.title}".`)
      setTrackToDelete(null)
      await loadDashboard(filters)
    } finally {
      setDeleting(false)
    }
  }

  const confirmDeleteGroup = async (): Promise<void> => {
    if (!groupToDelete) {
      return
    }

    setDeleting(true)
    try {
      await deleteAudioGroup(groupToDelete.id)
      toast(`Deleted audio group "${groupToDelete.name}".`)
      setGroupToDelete(null)
      await loadDashboard(filters)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div
        className={[
          'audio-page',
          tab === 'tracks' ? cx('audio-page-tracks', styles.tracksPage) : '',
          tab === 'tracks' && playingTrack ? cx('audio-page-tracks-player', styles.tracksPlayer) : '',
          tab === 'groups' ? 'audio-page-groups' : '',
          tab === 'assignments' ? cx('audio-page-assignments', styles.assignmentsPage) : '',
          tab === 'assignments' && playingTrack ? cx('audio-page-assignments-player', styles.assignmentsPlayer) : ''
        ].filter(Boolean).join(' ')}
      >
        <div className={cx('page-header', 'audio-page-header', styles.header)}>
          <div className={cx('audio-heading-copy', styles.headingCopy)}>
            <h1 className="page-title">Manage Audio</h1>
            <p className="page-subtitle">
              Upload and organize audio tracks into groups for channel playback.
            </p>
          </div>
          <div className={cx('page-header-actions', styles.headerActions)}>
            <Tabs
              ariaLabel="Audio sections"
              className={cx('audio-page-tabs', styles.tabs)}
              items={[
                { label: 'Tracks', to: '/admin/audio' },
                { label: 'Groups', to: '/admin/audio/groups' },
                { label: 'Assignments', to: '/admin/audio/assignments' }
              ]}
            />
            {!isMobileAudioPage && (
              <Button className={cx('audio-refresh-desktop', styles.refreshDesktop)} mobileFullWidth onClick={() => void refresh()}>
                <RefreshCw size={16} /> Refresh
              </Button>
            )}
          </div>
        </div>

        {tab === 'tracks' ? (
          <AudioTracks
            filters={filters}
            groups={groups}
            items={visibleTracks}
            labels={labels}
            loading={tracksLoading}
            loadingTrackId={audioLoading ? playingTrack?.id ?? null : null}
            onDelete={async (track) => setTrackToDelete(track)}
            onEdit={setSelectedTrack}
            onFilter={applyAudioFilters}
            onManageGroups={setBindingTrack}
            onPlayTrack={playTrack}
            onReprocess={async (track) => {
              const queuedTrack = await reprocessAudioTrack(track.id)
              updateVisibleTrack(queuedTrack)
              toast(`Queued "${track.title}" for processing.`)
              void refreshTrackAfterReprocess(track.id)
            }}
            onFindUploadPromptMatch={findMatchingSunoPrompt}
            onRegisterMobileUploadAction={registerMobileUploadAction}
            onSuggestUploadPromptLabels={async (stylePrompt) => {
              const result = await suggestAudioLabelsFromPrompt({ style_prompt: stylePrompt })
              toast(`Suggested ${result.items.length} batch label(s).`)
              return result.items
            }}
            onApplyUploadLabels={async (track, labels) => {
              await updateAudioTrackLabels(track.id, labels)
            }}
            onPersistUploadGroups={async (track, groupIds) => {
              const updatedTrack = await updateAudioTrackGroups(track.id, groupIds)
              updateVisibleTrack(updatedTrack)
              return updatedTrack
            }}
            onPersistUploadPrompt={async (track, stylePrompt) => {
              const updatedTrack = await updateAudioTrack({
                id: track.id,
                title: track.title,
                artist: track.artist,
                style_prompt: stylePrompt
              })
              updateVisibleTrack(updatedTrack)
              return updatedTrack
            }}
            onPersistUploadPromptMatch={async (tracks, prompt) => {
              const trackIds = tracks.map((track) => track.id)
              const result = await linkSunoPromptTracks({
                promptId: prompt.id,
                trackIds
              })

              if (result.linkedCount === 0) {
                throw new Error('Uploaded tracks could not be linked to this prompt.')
              }

              const updatedTracks = await Promise.all(trackIds.map(getAudioTrack))
              updatedTracks.forEach(updateVisibleTrack)
              return updatedTracks
            }}
            onUploadComplete={async () => {
              await loadDashboard(filters)
            }}
            onUploadFile={async (file, groupIds, stylePrompt, sunoPromptId, onProgress) => {
              return uploadAudioFile(file, groupIds, stylePrompt, sunoPromptId, onProgress)
            }}
            onUploadStateChange={setUploading}
            playingTrackId={audioPlaying ? playingTrack?.id ?? null : null}
            totalItems={totalTracks}
            uploading={uploading}
          />
        ) : tab === 'groups' ? (
          <AudioGroups
            groups={groups}
            onCreate={openCreateGroupDialog}
            onDelete={async (group) => setGroupToDelete(group)}
            onEdit={setSelectedGroup}
            onRegisterMobileCreateAction={registerMobileCreateGroupAction}
          />
        ) : (
          <AudioAssignments
            groups={groups}
            labels={labels}
            loadingTrackId={audioLoading ? playingTrack?.id ?? null : null}
            onError={toast}
            onLoadBindings={getAudioTrackBindings}
            onPlayTrack={(track) => playStandaloneTrack(toPlayableAudioTrack(track))}
            onToggleBinding={updateAudioTrackBinding}
            playingTrackId={audioPlaying ? playingTrack?.id ?? null : null}
          />
        )}
      </div>

      {selectedTrack && (
        <AudioTrackDialog
          groups={groups}
          labels={labels}
          promptGroups={sunoPromptGroups}
          track={selectedTrack}
          onClose={() => setSelectedTrack(null)}
          onSave={async (input) => {
            await updateAudioTrack(input)
            const groupedTrack = await updateAudioTrackGroups(input.id, input.audioGroupIds)
            const track = input.manual_bpm === null
              ? await clearAudioTrackBpm(input.id)
              : await updateAudioTrackBpm({
                  id: input.id,
                  bpm: input.manual_bpm,
                  source: input.manual_bpm_source ?? 'number'
                })
            const savedLabels = await updateAudioTrackLabels(input.id, input.labels)
            updateVisibleTrack({
              ...track,
              groups: groupedTrack.groups,
              labels: savedLabels
                .filter((assignment) => assignment.status === 'approved')
                .map(toTrackLabelSummary),
              title: input.title,
              artist: input.artist,
              style_prompt: input.style_prompt
            })
            toast(`Updated track "${input.title}".`)
            setSelectedTrack(null)
          }}
          onDeleteImage={async (trackId) => {
            const track = await deleteAudioTrackImage(trackId)
            updateVisibleTrack(track)
            toast('Track image removed.')
            return track
          }}
          onCheckPromptLibrary={async (input) => {
            const matchedPrompt = await findMatchingSunoPrompt(input.style_prompt)

            if (!matchedPrompt) {
              throw new Error('No exact Prompt Library match found.')
            }

            await updateAudioTrack({
              id: input.id,
              title: input.title,
              artist: input.artist,
              style_prompt: input.style_prompt
            })
            await unlinkAudioTrackSunoPrompt(input.id)
            const result = await linkSunoPromptTracks({
              promptId: matchedPrompt.id,
              trackIds: [input.id]
            })

            if (result.linkedCount === 0) {
              throw new Error('Track could not be linked to this prompt.')
            }

            const track = await getAudioTrack(input.id)
            updateVisibleTrack(track)
            setSelectedTrack(track)
            toast(`Linked "${track.title}" to "${matchedPrompt.title}".`)

            return track
          }}
          onSavePromptFromTrack={async (input: { prompt: PromptEditorState; trackId: string }) => {
            const { prompt, trackId } = input
            const stylePromptText = prompt.stylePrompt.trim()
            const excludePromptText = prompt.excludePrompt.trim() || null

            if (!stylePromptText) {
              throw new Error('Style prompt is required.')
            }

            const matchedPrompt = await findMatchingSunoPrompt(stylePromptText, excludePromptText)

            if (matchedPrompt) {
              throw new Error(`A prompt matching this style already exists: "${matchedPrompt.title}". Use "Check library" to link it.`)
            }

            const createdPrompt = await createSunoPrompt({
              title: prompt.title.trim(),
              description: prompt.description.trim() || null,
              sunoGroupIds: prompt.groupIds,
              style_prompt: stylePromptText,
              exclude_prompt: excludePromptText
            })

            await unlinkAudioTrackSunoPrompt(trackId)
            const result = await linkSunoPromptTracks({
              promptId: createdPrompt.id,
              trackIds: [trackId]
            })

            if (result.linkedCount === 0) {
              throw new Error('Track could not be linked to the new prompt.')
            }

            const track = await getAudioTrack(trackId)
            updateVisibleTrack(track)
            setSelectedTrack(track)
            toast(`Created and linked "${track.title}" to "${createdPrompt.title}".`)

            return track
          }}
          onSuggestLabels={async (trackId, stylePrompt) => {
            const result = await suggestAudioTrackLabels({
              audioTrackId: trackId,
              style_prompt: stylePrompt
            })
            toast(`Suggested ${result.items.length} label(s).`)
            return result.items
          }}
          onLoadTrackLabels={getAudioTrackLabels}
          onRejectLabel={async (trackId, assignmentId) => {
            await rejectAudioTrackLabel(trackId, assignmentId)
          }}
          onAnalyzeAudio={async (trackId) => {
            const result = await analyzeAudioTrackLabels(trackId)
            toast('Essentia label analysis started.')
            return result.audio_analysis
          }}
          onDeleteLosslessSource={async (trackId) => {
            const track = await deleteAudioTrackLosslessSource(trackId)
            updateVisibleTrack(track)
            setSelectedTrack(track)
            toast('FLAC removed.')
            return track
          }}
          onUploadImage={async (trackId, file) => {
            const track = await uploadAudioTrackImage(trackId, file)
            updateVisibleTrack(track)
            toast('Track image updated.')
            return track
          }}
          onUploadLosslessSource={async (trackId, file) => {
            const track = await uploadAudioTrackLosslessSource(trackId, file)
            updateVisibleTrack(track)
            setSelectedTrack(track)
            toast('Lossless source queued.')
            return track
          }}
          onUnlinkSunoPrompt={async (trackId) => {
            await unlinkAudioTrackSunoPrompt(trackId)
            const track = await getAudioTrack(trackId)
            updateVisibleTrack(track)
            setSelectedTrack(track)
            toast(`Unlinked "${track.title}" from library prompt.`)
            return track
          }}
        />
      )}

      {bindingTrack && (
        <AudioBindDialog
          groups={groups}
          track={bindingTrack}
          onClose={() => setBindingTrack(null)}
          onSave={async (audioGroupIds) => {
            const track = await updateAudioTrackGroups(bindingTrack.id, audioGroupIds)
            updateVisibleTrack(track)
            toast(`Updated groups for "${bindingTrack.title}".`)
            setBindingTrack(null)
          }}
        />
      )}

      {selectedGroup && (
        <AudioGroupDialog
          group={selectedGroup}
          onClose={() => setSelectedGroup(null)}
          onSave={async (input) => {
            if (input.id) {
              await updateAudioGroup(input)
              toast(`Updated audio group "${input.name}".`)
            } else {
              await createAudioGroup(input)
              toast(`Created audio group "${input.name}".`)
            }

            setSelectedGroup(null)
            await loadDashboard(filters)
          }}
        />
      )}

      {playingTrack && tab === 'groups' && <div className={pc('admin-audio-player-spacer')} />}

      {trackToDelete && (
        <DeleteConfirmDialog
          description="This will remove the track from the audio library and delete its stored audio file when no other track uses it. This action cannot be undone."
          isDeleting={deleting}
          itemName={trackToDelete.artist ? `${trackToDelete.artist} - ${trackToDelete.title}` : trackToDelete.title}
          onCancel={() => setTrackToDelete(null)}
          onConfirm={confirmDeleteTrack}
          title="Delete Audio Track"
        />
      )}

      {groupToDelete && (
        <DeleteConfirmDialog
          description="This will delete the audio group and remove its channel and track bindings. The tracks themselves stay in the library. This action cannot be undone."
          isDeleting={deleting}
          itemName={groupToDelete.name}
          onCancel={() => setGroupToDelete(null)}
          onConfirm={confirmDeleteGroup}
          title="Delete Audio Group"
        />
      )}
    </>
  )
}
